import { Check, Gavel, ScrollText, ShieldAlert, X } from 'lucide-react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import {
  COC_DISCIPLINARY,
  COC_PENALTIES,
  COC_PLAGIARISM_DEFINITION,
  COC_PLAGIARISM_LEVELS,
  COC_SHALL,
  COC_SHALL_NOT,
} from '@/data/about';

/**
 * CodeOfConduct (/about — section 8 ★ accreditation-mandated) — "Code of Conduct".
 *
 * Renders the Student Code of Conduct entirely from COC_* in '@/data/about';
 * every institutional string is verbatim. A two-column duties block pairs
 * "Every student shall" (COC_SHALL, check glyph) with "Every student shall not"
 * (COC_SHALL_NOT, cross glyph) as plain hover-lift cards. To manage length, the
 * penalties, disciplinary procedure and plagiarism policy sit inside accessible
 * <details> disclosures — the verbatim content is always present in the DOM.
 *
 * Motion: SplitReveal headline, ScrollReveal ledes and list-child staggers. All
 * primitives are reduced-motion safe and never strand content hidden. No fact,
 * figure or wording is invented beyond short connective microcopy.
 */
export function CodeOfConduct() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">Conduct</p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Code of Conduct
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              The IBAS Student Code of Conduct sets the responsibilities every student accepts on
              enrolment, the conduct that is not permitted, and the procedures that follow a breach.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Duties: shall · shall not ───────────────────────────────── */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Every student shall */}
          <div className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0">
            <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
              Every student shall
            </p>
            <ScrollReveal as="ul" stagger={0.07} className="mt-5 space-y-4">
              {COC_SHALL.map((duty) => (
                <li key={duty} className="flex items-start gap-3">
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                    <Check aria-hidden className="size-4" />
                  </span>
                  <span className="text-sm leading-relaxed text-foreground">{duty}</span>
                </li>
              ))}
            </ScrollReveal>
          </div>

          {/* Every student shall not */}
          <div className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0">
            <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
              Every student shall not
            </p>
            <ScrollReveal as="ul" stagger={0.07} className="mt-5 space-y-4">
              {COC_SHALL_NOT.map((rule) => (
                <li key={rule} className="flex items-start gap-3">
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                    <X aria-hidden className="size-4" />
                  </span>
                  <span className="text-sm leading-relaxed text-foreground">{rule}</span>
                </li>
              ))}
            </ScrollReveal>
          </div>
        </div>

        {/* ── Disclosures: penalties · procedure · plagiarism ─────────── */}
        <div className="mt-12 space-y-4">
          {/* (c) Penalties */}
          <details className="group rounded-2xl border border-border bg-muted/30 open:bg-background">
            <summary className="flex cursor-pointer list-none items-center gap-4 p-6 [&::-webkit-details-marker]:hidden">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                <Gavel aria-hidden className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-lg leading-tight font-semibold text-foreground">
                  Breach of the Code — penalties
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">
                  Measures that may follow a breach of the Code.
                </span>
              </span>
              <span
                aria-hidden
                className="font-display text-2xl text-brand-navy transition-transform duration-300 group-open:rotate-45 motion-reduce:transition-none"
              >
                +
              </span>
            </summary>
            <div className="px-6 pb-6">
              <ul className="flex flex-wrap gap-2.5">
                {COC_PENALTIES.map((penalty) => (
                  <li
                    key={penalty}
                    className="rounded-full border border-border bg-background px-4 py-2 text-sm leading-snug text-foreground"
                  >
                    {penalty}
                  </li>
                ))}
              </ul>
            </div>
          </details>

          {/* (d) Disciplinary procedure */}
          <details className="group rounded-2xl border border-border bg-muted/30 open:bg-background">
            <summary className="flex cursor-pointer list-none items-center gap-4 p-6 [&::-webkit-details-marker]:hidden">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                <ScrollText aria-hidden className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-lg leading-tight font-semibold text-foreground">
                  Disciplinary procedure
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">
                  How reports are investigated, heard and appealed.
                </span>
              </span>
              <span
                aria-hidden
                className="font-display text-2xl text-brand-navy transition-transform duration-300 group-open:rotate-45 motion-reduce:transition-none"
              >
                +
              </span>
            </summary>
            <div className="px-6 pb-6">
              <ol className="space-y-4">
                {COC_DISCIPLINARY.map((point, index) => (
                  <li key={point} className="flex items-start gap-4">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-navy text-xs font-semibold text-white tabular-nums">
                      {index + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-foreground">{point}</span>
                  </li>
                ))}
              </ol>
            </div>
          </details>

          {/* (e) Plagiarism policy */}
          <details className="group rounded-2xl border border-border bg-muted/30 open:bg-background">
            <summary className="flex cursor-pointer list-none items-center gap-4 p-6 [&::-webkit-details-marker]:hidden">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                <ShieldAlert aria-hidden className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-lg leading-tight font-semibold text-foreground">
                  Plagiarism policy
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">
                  How plagiarism is defined and the graded infraction levels.
                </span>
              </span>
              <span
                aria-hidden
                className="font-display text-2xl text-brand-navy transition-transform duration-300 group-open:rotate-45 motion-reduce:transition-none"
              >
                +
              </span>
            </summary>
            <div className="px-6 pb-6">
              <p className="text-sm leading-relaxed text-muted-foreground">
                {COC_PLAGIARISM_DEFINITION}
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                {COC_PLAGIARISM_LEVELS.map((item) => (
                  <div
                    key={item.level}
                    className="h-full rounded-2xl border border-border bg-background p-6 shadow-sm"
                  >
                    <p className="font-label text-xs tracking-[0.18em] text-brand-gold uppercase">
                      {item.level}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-foreground">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </details>
        </div>
      </div>
    </section>
  );
}
